import React, { Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lightbulb, GraduationCap } from 'lucide-react';
import { type ModuleId, MODULES, getModuleIntro, getModuleHexColor, getModuleIcon } from '../../content';
import { GroupId } from '../../config/systemConfig';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { useBodyScrollLock } from '../../app/hooks/useBodyScrollLock';
import { useSwipeToDismiss } from '../../app/hooks/useSwipeToDismiss';
interface ModuleIntroDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId: ModuleId;
  selectedGroup: GroupId;
}
export function ModuleIntroDrawer({
  isOpen,
  onClose,
  moduleId,
  selectedGroup
}: ModuleIntroDrawerProps) {
  const focusTrapRef = useFocusTrap(isOpen);
  useEscapeKey(onClose, isOpen);
  useBodyScrollLock(isOpen);
  const swipe = useSwipeToDismiss({
    onDismiss: onClose,
    disabled: !isOpen
  });
  const module = MODULES.find(m => m.id === moduleId);
  const moduleIntro = getModuleIntro(moduleId, selectedGroup);
  if (!module || !moduleIntro) return null;
  const IconComponent = getModuleIcon(module.icon);
  const moduleHex = getModuleHexColor(module.color);
  return <AnimatePresence data-id="element-1605" data-mp="2sdnuw6tr7gzr">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40" data-id="element-1606" data-mp="2nekrsrxc3kuf" />

          {/* Bottom Sheet */}
          <motion.div ref={focusTrapRef} role="dialog" aria-modal="true" aria-labelledby="intro-title" initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 32,
        stiffness: 350
      }} {...swipe.dragProps} className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col" data-id="element-1607" data-mp="7lbyd1cg3zb4">
            {/* Drag Handle — only this region initiates swipe-to-dismiss
                so the inner scrollable content keeps scrolling on touch. */}
            <div {...swipe.handleProps} onClick={onClose} className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing" role="button" aria-label="Sleep omlaag of tik om te sluiten" data-id="element-1608" data-mp="1ibqklm7lc5a6">
              <div className="w-10 h-1 rounded-full bg-slate-300" data-id="element-1609" data-mp="2ysw4jy07k6ma" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 pb-4 border-b border-slate-100" data-id="element-1610" data-mp="33p3b9t0fhm5a">
              <div className="flex items-center gap-3" data-id="element-1611" data-mp="s1q4z0ae8way">
                <div className="p-2 rounded-xl shadow-sm border" style={{
              backgroundColor: `${moduleHex}15`,
              borderColor: `${moduleHex}30`,
              color: moduleHex
            }} data-id="element-1612" data-mp="n5sl4jzx97tf">
                  <IconComponent className="w-6 h-6" strokeWidth={2.5} data-id="element-1613" data-mp="1ataiqw5vb04b" />
                </div>
                <div data-id="element-1614" data-mp="15w0ismkur628">
                  <h2 id="intro-title" className="text-xl font-black text-slate-900 leading-tight" data-id="element-1615" data-mp="15ufsj95km9a6">
                    Wat leer je hier?
                  </h2>
                  <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mt-0.5" data-id="element-1616" data-mp="1frc11evgyveq">
                    {module.title}
                  </p>
                </div>
              </div>

              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600" aria-label="Sluiten" data-id="element-1617" data-mp="3hmus7pqc5lso">
                <X className="w-5 h-5" data-id="element-1618" data-mp="2im0ynyzkouwl" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain p-6" data-id="element-1619" data-mp="2mnw156t8peby">
              <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-1620" data-mp="1pxbtnovec5fk">
                <div className="flex items-center gap-2 mb-4" data-id="element-1621" data-mp="1pvipqhc63zb9">
                  <GraduationCap className="w-5 h-5 text-slate-400" data-id="element-1622" data-mp="1ux7ytrfty2wp" />
                  <h3 className="text-base font-bold text-slate-800" data-id="element-1623" data-mp="1yxrxx0jyrglh">
                    Onderwerpen in deze module
                  </h3>
                </div>

                <ul className="space-y-3" data-id="element-1624" data-mp="23ql3trr64uz5">
                  {moduleIntro.topics.map((topic, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-1625" data-mp="1b8frg8g0tmmv" data-mpi={idx}>
                      <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{
                  backgroundColor: moduleHex
                }} data-id="element-1626" data-mp="1b6mjmpto8wda" />
                      <span className="text-sm font-medium text-slate-700 leading-relaxed" data-id="element-1627" data-mp="unzy1w5npuzt">
                        {topic}
                      </span>
                    </li>)}
                </ul>
              </div>

              {moduleIntro.tip && <div className="mt-4 bg-amber-50 border border-amber-100 rounded-2xl p-4 flex gap-3" data-id="element-1628" data-mp="ks5b4ouehdr8">
                  <div className="shrink-0 mt-0.5" data-id="element-1629" data-mp="fxix0dwxp73g">
                    <div className="bg-white p-1.5 rounded-lg shadow-sm text-amber-500 border border-amber-100" data-id="element-1630" data-mp="b1alrbluxy1k">
                      <Lightbulb className="w-4 h-4" data-id="element-1631" data-mp="708m4to13jz2" />
                    </div>
                  </div>
                  <div data-id="element-1632" data-mp="1z1fixxmgqtz">
                    <h4 className="font-bold text-amber-900 text-sm mb-1" data-id="element-1633" data-mp="21f72wt3fkws">
                      Tip van Meester Djay
                    </h4>
                    <p className="text-sm font-medium text-amber-800/80 leading-relaxed" data-id="element-1634" data-mp="3ji07fmygn3m9">
                      {moduleIntro.tip}
                    </p>
                  </div>
                </div>}

              {/* Bottom padding */}
              <div className="h-6" data-id="element-1635" data-mp="39m6ptp5vwjxm" />
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}